import React, { useState, useEffect } from 'react';
import { FLOORS, FLOOR_LAYOUTS, ASSET_TYPES, ASSET_STATUSES } from '../mockData';
import AssetDetailPanel from '../components/AssetDetailPanel';
import { Filter, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { toast } from '../components/ui/sonner';
import AssetIcon from '../components/AssetIcon';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const Map = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [currentFloor, setCurrentFloor] = useState(location.state?.floor || 0);
  const [assets, setAssets] = useState([]);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [showFilters, setShowFilters] = useState(true);
  const [highlightedAsset, setHighlightedAsset] = useState(location.state?.assetId || null);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Filter states
  const [typeFilters, setTypeFilters] = useState(ASSET_TYPES.map(t => t.value));
  const [statusFilters, setStatusFilters] = useState(ASSET_STATUSES.map(s => s.value));
  const [floorFilters, setFloorFilters] = useState(FLOORS.map(f => f.id));

  // Load assets from localStorage on mount
  useEffect(() => {
    const storedAssets = localStorage.getItem('e2-assets');
    if (storedAssets) {
      setAssets(JSON.parse(storedAssets));
    } else {
      // Load mock data
      const { MOCK_ASSETS } = require('../mockData');
      setAssets(MOCK_ASSETS);
      localStorage.setItem('e2-assets', JSON.stringify(MOCK_ASSETS));
    }
  }, []);

  // Handle navigation from "Show on Map"
  useEffect(() => {
    if (location.state?.assetId) {
      setHighlightedAsset(location.state.assetId);
      setTimeout(() => setHighlightedAsset(null), 3000);
    }
  }, [location.state]);

  // Simple simulation: move 1-2 assets slightly
  useEffect(() => {
    const interval = setInterval(() => {
      setAssets(prev => {
        const updated = [...prev];
        const movableIndexes = updated
          .map((asset, idx) => ({ asset, idx }))
          .filter(({ asset }) => asset.status === 'in-use' && asset.floor === currentFloor)
          .slice(0, 2);
        
        movableIndexes.forEach(({ idx }) => {
          const offsetX = (Math.random() - 0.5) * 3;
          const offsetY = (Math.random() - 0.5) * 3;
          updated[idx] = {
            ...updated[idx],
            x: updated[idx].x + offsetX,
            y: updated[idx].y + offsetY
          };
        });
        
        if (movableIndexes.length > 0) {
          localStorage.setItem('e2-assets', JSON.stringify(updated));
        }
        
        return updated;
      });
    }, 5000);

    return () => clearInterval(interval);
  }, [currentFloor]);

  const floorLayout = FLOOR_LAYOUTS[currentFloor];
  const floorAssets = assets.filter(a => 
    a.floor === currentFloor &&
    typeFilters.includes(a.type) &&
    statusFilters.includes(a.status) &&
    floorFilters.includes(a.floor) &&
    (searchQuery === '' || a.name.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const handleStatusUpdate = (assetId, newStatus, tickets = null) => {
    setAssets(prev => {
      const updated = prev.map(a => {
        if (a.id === assetId) {
          const updatedAsset = { 
            ...a, 
            status: newStatus, 
            lastSeen: new Date().toISOString() 
          };
          if (tickets !== null) {
            updatedAsset.maintenanceTickets = tickets;
          }
          return updatedAsset;
        }
        return a;
      });
      localStorage.setItem('e2-assets', JSON.stringify(updated));
      return updated;
    });
    
    if (selectedAsset && selectedAsset.id === assetId) {
      const updatedSelectedAsset = { 
        ...selectedAsset, 
        status: newStatus,
        lastSeen: new Date().toISOString()
      };
      if (tickets !== null) {
        updatedSelectedAsset.maintenanceTickets = tickets;
      }
      setSelectedAsset(updatedSelectedAsset);
    }
    
    const statusLabel = ASSET_STATUSES.find(s => s.value === newStatus)?.label;
    toast.success(`Status updated to ${statusLabel}`);
  };

  const handleShowOnMap = (asset) => {
    setCurrentFloor(asset.floor);
    setHighlightedAsset(asset.id);
    setTimeout(() => setHighlightedAsset(null), 3000);
    toast.info(`Showing ${asset.name} on Floor ${asset.floor}`);
  };

  const handleReplayPath = (asset) => {
    toast.info(`Replaying movement path for ${asset.name}`);
    // Simple implementation: just show message
  };

  const handleFindSimilar = (asset) => {
    const similar = assets.filter(a => 
      a.type === asset.type && 
      a.floor === asset.floor && 
      a.id !== asset.id
    );
    toast.info(`Found ${similar.length} similar ${asset.type}(s) on this floor`);
  };

  const toggleTypeFilter = (type) => {
    setTypeFilters(prev => 
      prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
    );
  };

  const toggleStatusFilter = (status) => {
    setStatusFilters(prev => 
      prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]
    );
  };

  const toggleFloorFilter = (floor) => {
    setFloorFilters(prev => 
      prev.includes(floor) ? prev.filter(f => f !== floor) : [...prev, floor]
    );
  };

  return (
    <div className="map-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">{t('map.title')}</h1>
          <p className="page-subtitle">{t('map.subtitle')}</p>
        </div>
        <Button 
          variant="outline" 
          onClick={() => setShowFilters(!showFilters)}
          className="filter-toggle"
        >
          <Filter size={18} />
          {showFilters ? t('map.hideFilters') : t('map.showFilters')}
        </Button>
      </div>

      <div className="map-content">
        {/* Filters Sidebar */}
        {showFilters && (
          <div className="filters-sidebar">
            <div className="filter-section">
              <div className="search-box">
                <Search size={18} className="search-icon" />
                <Input
                  type="text"
                  placeholder={t('map.searchAssets')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="search-input"
                />
              </div>
            </div>

            <div className="filter-section">
              <h3 className="filter-title">{t('map.assetType')}</h3>
              {ASSET_TYPES.map(type => (
                <div key={type.value} className="filter-item">
                  <Checkbox
                    id={`type-${type.value}`}
                    checked={typeFilters.includes(type.value)}
                    onCheckedChange={() => toggleTypeFilter(type.value)}
                  />
                  <Label htmlFor={`type-${type.value}`}>{t(`assetTypes.${type.value}`)}</Label>
                </div>
              ))}
            </div>

            <div className="filter-section">
              <h3 className="filter-title">{t('map.status')}</h3>
              {ASSET_STATUSES.map(status => (
                <div key={status.value} className="filter-item">
                  <Checkbox
                    id={`status-${status.value}`}
                    checked={statusFilters.includes(status.value)}
                    onCheckedChange={() => toggleStatusFilter(status.value)}
                  />
                  <Label htmlFor={`status-${status.value}`}>
                    <span className="status-dot" style={{ backgroundColor: status.color }} />
                    {t(`status.${status.value.replace(/-/g, '')}`)}
                  </Label>
                </div>
              ))}
            </div>

            <div className="filter-section">
              <h3 className="filter-title">{t('map.floor')}</h3>
              {FLOORS.map(floor => (
                <div key={floor.id} className="filter-item">
                  <Checkbox
                    id={`floor-${floor.id}`}
                    checked={floorFilters.includes(floor.id)}
                    onCheckedChange={() => toggleFloorFilter(floor.id)}
                  />
                  <Label htmlFor={`floor-${floor.id}`}>
                    {floor.name} - {floor.department}
                  </Label>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Map Area */}
        <div className="map-area">
          {/* Floor Selector */}
          <div className="floor-selector">
            {FLOORS.map(floor => (
              <button
                key={floor.id}
                className={`floor-button ${currentFloor === floor.id ? 'active' : ''}`}
                onClick={() => setCurrentFloor(floor.id)}
              >
                <div className="floor-name">{floor.name}</div>
                <div className="floor-dept">{floor.department}</div>
              </button>
            ))}
          </div>

          {/* SVG Floor Plan */}
          <div className="floor-plan-container">
            <svg viewBox="0 0 630 310" className="floor-plan-svg">
              {/* Corridors */}
              {floorLayout.corridors.map((corridor, idx) => (
                <rect
                  key={`corridor-${idx}`}
                  x={corridor.x}
                  y={corridor.y}
                  width={corridor.width}
                  height={corridor.height}
                  className="corridor"
                />
              ))}

              {/* Rooms */}
              {floorLayout.rooms.map(room => (
                <g key={room.id}>
                  <rect
                    x={room.x}
                    y={room.y}
                    width={room.width}
                    height={room.height}
                    className="room"
                  />
                  <text
                    x={room.x + room.width / 2}
                    y={room.y + room.height / 2 - 5}
                    className="room-id"
                  >
                    {room.id}
                  </text>
                  <text
                    x={room.x + room.width / 2}
                    y={room.y + room.height / 2 + 10}
                    className="room-name"
                  >
                    {room.name}
                  </text>
                </g>
              ))}

              {/* Assets */}
              {floorAssets.map(asset => {
                const statusInfo = ASSET_STATUSES.find(s => s.value === asset.status);
                const isHighlighted = highlightedAsset === asset.id;
                
                const handleAssetClick = (e) => {
                  e.stopPropagation();
                  console.log('Asset clicked:', asset.name);
                  setSelectedAsset(asset);
                };
                
                return (
                  <g
                    key={asset.id}
                    transform={`translate(${asset.x}, ${asset.y})`}
                    className={`asset-marker ${isHighlighted ? 'highlighted' : ''}`}
                    onClick={handleAssetClick}
                    style={{ cursor: 'pointer' }}
                  >
                    <circle
                      r="14"
                      fill={statusInfo.color}
                      className="asset-circle"
                    />
                    <foreignObject 
                      x="-10" 
                      y="-10" 
                      width="20" 
                      height="20"
                      style={{ pointerEvents: 'none' }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        <AssetIcon type={asset.type} size={14} color="white" strokeWidth={2.5} />
                      </div>
                    </foreignObject>
                  </g>
                );
              })}
            </svg>
          </div>

          {/* Asset Count */}
          <div className="asset-count">
            {t('map.showing')} {floorAssets.length} {t('map.assetOn')} {FLOORS.find(f => f.id === currentFloor)?.department}
          </div>
        </div>
      </div>

      {/* Asset Detail Panel */}
      {selectedAsset && (
        <AssetDetailPanel
          asset={selectedAsset}
          onClose={() => setSelectedAsset(null)}
          onStatusUpdate={handleStatusUpdate}
          onReplayPath={handleReplayPath}
          onFindSimilar={handleFindSimilar}
        />
      )}
    </div>
  );
};

export default Map;
